import { Icon, Icons, Intent } from "construct-ui";
import QueryListComponent from "../../components/query-list/query-list.component";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import codeMasterModel from "../../models/common/code-master.model";
import languageModel from "../../models/language.model";
import loginModel from "../../models/login.model";
import ViewBase from "../view-base.view";
import CommonCodeAddEditDialog from "./common-code-add-edit.dialog";

/**
 * Common Code - Base View
 * 
 * Base code master page for both subsidiary admins and system admins to easily add code into the database
 * 
 * @author Darrus
 */
export default function CommonCodeView() {
  ViewBase.call(this);

  const CODEMASTER_GLOBAL_CODE = 'global';
  const DISPLAY_HEIGHT_SYSTEM_ADMIN = '600px';
  const DISPLAY_HEIGHT_SUBSIDIARY_ADMIN = '710px';
  const FILTER_PANEL_WIDTH = '260px';

  let systemAdminView = false;

  const CODEMASTER_TYPE = {
    GLOBAL: 0,
    COUNTRY: 1
  };

  const CODEMASTER_TYPE_LABEL = [
    { get name() { return languageModel.getText('LT00000788', 'Global'); } },
    { get name() { return languageModel.getText('LT00000188', 'Country'); } }
  ];

  let isDialogOpen = false;

  let codemasterCategories = [];
  let codemasterCategoryData = [];

  let selectedCodemasterType = CODEMASTER_TYPE.COUNTRY;
  let selectedCodemasterCategory = null;
  let selectedCodemasterDetailCode = '';

  let countries = [];
  let selectedCountry = null;

  const resetSelection = () => {
    codemasterCategoryData = [];
    selectedCodemasterCategory = null;
  }

  const getCodemasterList = (codemasterType) => {
    return codeMasterModel.getCodemasterList(getCountryCode(codemasterType))
      .then((result) => {
        codemasterCategories = structuredClone(result);
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  const getCodemasterCategoryData = (codemasterType, category) => {
    codemasterCategoryData = [];
    return codeMasterModel.getCodemasterCategoryData(getCountryCode(codemasterType), category)
      .then(result => {
        codemasterCategoryData = result;
      })
      .catch(error => {
        this.handleError(error);
      })
  }

  const getCountries = () => {
    return codeMasterModel.getByCategoryV2({ category: 'country' })
      .then(result => {
        countries = result;
      })
      .catch(error => {
        this.handleError(error);
      });
  }

  const getCountryCode = (codemasterType) => {
    if (codemasterType === CODEMASTER_TYPE.GLOBAL) {
      return CODEMASTER_GLOBAL_CODE;
    } else {
      return selectedCountry;
    }
  }

  const selectCategory = (category) => {
    selectedCodemasterCategory = category;
    getCodemasterCategoryData(selectedCodemasterType, selectedCodemasterCategory.code);
  }

  const editCodemasterDetail = (detail) => {
    selectedCodemasterDetailCode = detail.code;
    isDialogOpen = true;
  }

  const addCodemasterDetail = () => {
    selectedCodemasterDetailCode = '';
    isDialogOpen = true;
  }

  const showDeleteMessage = (codemasterType, category, code, name) => {
    this.showConfirm(
      this.getText('EM00000265', 'Are you sure you want to delete?'),
      `${this.getText('LT00000135', 'Delete')} - ${name} (${code})`,
      (action) => {
        if (action === 'YES') {
          deleteCodemasterDetail(codemasterType, category, code);
        }
      }
    )
  }

  const deleteCodemasterDetail = (codemasterType, category, code) => {
    return codeMasterModel.deleteCodemasterDetail(getCountryCode(codemasterType), category, code)
      .then(() => { getCodemasterCategoryData(codemasterType, category) })
      .catch(error => {
        this.handleError(error);
      });
  }

  const onDialogClose = () => {
    isDialogOpen = false;
    getCodemasterCategoryData(selectedCodemasterType, selectedCodemasterCategory.code);
  }

  return {
    oninit: ({ attrs: { systemAdminView: _systemAdminView = false } }) => {
      systemAdminView = _systemAdminView;
      selectedCountry = loginModel.credentials.country;

      if (systemAdminView) {
        getCountries();
        selectedCodemasterType = CODEMASTER_TYPE.GLOBAL;
      }

      getCodemasterList(selectedCodemasterType);
    },
    view: () => [
      systemAdminView && [
        m('.d-flex.gap-2',
          CODEMASTER_TYPE_LABEL.map(
            (type, index) => m(`a.btn${selectedCodemasterType === index ? '.btn-primary' : '.btn-outline-primary'}`, {
              onclick: () => {
                resetSelection();
                selectedCodemasterType = index;
                getCodemasterList(selectedCodemasterType);
              }
            }, type.name)
          )
        ),
        m('hr', { style: { 'background-color': 'var(--is-link-blue)' } }),
      ],
      m('.d-flex.flex-nowrap.gap-1', {
        style: {
          height: systemAdminView ? DISPLAY_HEIGHT_SYSTEM_ADMIN : DISPLAY_HEIGHT_SUBSIDIARY_ADMIN
        }
      },
        /**
         * TODO: Future enhancement: Make Filter Panel mobile responsive to fix small screen. 
         */
        m('.flex-shrink-0.h-100', { style: { width: FILTER_PANEL_WIDTH } },
          m('.card.h-100',
            m('.card-body.vstack.gap-3.h-100',
              systemAdminView && selectedCodemasterType === CODEMASTER_TYPE.COUNTRY &&
              m('div',
                m('label.form-label', this.getText('LT00000188', 'Country')),
                m(SingleSelectComponent, {
                  items: countries,
                  selectedItem: selectedCountry,
                  keyOnly: true,
                  onSelect: (selectedItem) => {
                    resetSelection();
                    selectedCountry = selectedItem;
                    getCodemasterList(selectedCodemasterType);
                  }
                })
              ),
              m('.flex-fill.vstack.h-100.overflow-hidden',
                m('label.form-label', this.getText('LT00000232', 'Filter')),
                m('.flex-fill.overflow-hidden',
                  m(QueryListComponent, {
                    items: codemasterCategories,
                    onSelect: selectCategory,
                    selectedItem: selectedCodemasterCategory,
                  })
                )
              )
            )
          )
        ),
        m('.flex-grow-1.h-100.overflow-auto',
          m('.card.h-100',
            m('.card-body.vstack.h-100',
              m('.flex-fill.overflow-auto',
                m('.table-responsive.m-0.h-100',
                  m('table.table',
                    m('thead',
                      m('tr',
                        m('th', this.getText('LT00000092', 'Action')),
                        m('th', this.getText('LT00000778', 'Code')),
                        m('th', this.getText('LT00000011', 'Name')),
                        m('th', this.getText('LT00000012', 'Description')),
                        m('th', this.getText('LT00000512', 'Sequence')),
                        m('th', this.getText('LT00000015', 'Status')),
                        m('th', this.getText('LT00000710', 'Updated By')),
                        m('th', this.getText('LT00000234', 'Last Updated Date')),
                      )
                    ),
                    m('tbody', { style: { 'overflow-y': 'auto' } },
                      codemasterCategoryData?.map((detail) => m('tr',
                        m('td',
                          m('.d-flex.gap-2',
                            m(Icon, { name: Icons.EDIT, onclick: () => { editCodemasterDetail(detail) }, intent: Intent.PRIMARY }),
                            m(Icon, { name: Icons.TRASH_2, onclick: () => { showDeleteMessage(selectedCodemasterType, selectedCodemasterCategory.code, detail.code, detail.name) }, intent: Intent.PRIMARY })
                          )
                        ),
                        m('td', detail?.code),
                        m('td.text-wrap', detail?.name),
                        m('td.text-wrap', detail?.description),
                        m('td', detail?.sequence),
                        m('td', detail?.status
                          ? m('.badge.rounded-pill.green-bg-status', this.getText('LT00000025', 'Active'))
                          : m('.badge.rounded-pill.grey-bg-status', this.getText('LT00000029', 'Inactive'))
                        ),
                        m('td', detail?.updateUserID),
                        m('td', detail?.updateDate)
                      ))
                    )
                  ),
                )
              ),
              m('.hstack.justify-content-center.card-footer',
                m('.btn.btn-primary', { onclick: addCodemasterDetail }, this.getText('LT00000090', 'Add'))
              )
            )
          )
        )
      ),
      isDialogOpen && m(CommonCodeAddEditDialog, {
        codemasterType: getCountryCode(selectedCodemasterType),
        codemasterCategory: selectedCodemasterCategory,
        codemasterDetailCode: selectedCodemasterDetailCode,
        onClose: onDialogClose
      }),
      this.getBaseViews()
    ]
  }
};
